/**
 * Local-only OCR using Tesseract.js (v5).
 *
 * PRIVACY: this module performs ALL recognition on-device. There are no
 * network or cloud calls — the legacy Claude-proxy and Google Vision paths
 * were intentionally removed. Language, core and wasm assets are self-hosted
 * (see OCR_ASSET_PATHS) so nothing is fetched from a third-party CDN.
 */

import { createWorker, type Worker } from 'tesseract.js';

/**
 * Paths to the self-hosted Tesseract assets. These files are copied into
 * `public/tesseract/` during build so they are served from the app's own
 * origin (no CDN, no external requests). Override before the first
 * recognizeText() call if your deployment serves them elsewhere.
 */
export const OCR_ASSET_PATHS = {
  workerPath: '/tesseract/worker.min.js',
  corePath: '/tesseract/tesseract-core.wasm.js',
  langPath: '/tesseract/lang',
};

const OCR_LANG = 'eng';

let workerPromise: Promise<Worker> | null = null;

// Lazily create (and reuse) a single English Tesseract worker.
function getWorker(): Promise<Worker> {
  if (!workerPromise) {
    workerPromise = createWorker(OCR_LANG, undefined, {
      workerPath: OCR_ASSET_PATHS.workerPath,
      corePath: OCR_ASSET_PATHS.corePath,
      langPath: OCR_ASSET_PATHS.langPath,
    });
  }
  return workerPromise;
}

/**
 * Recognize text from an image entirely on-device.
 * Reuses a shared worker across calls; call terminateOcr() to free it.
 */
export async function recognizeText(image: Blob | File | HTMLCanvasElement): Promise<string> {
  const worker = await getWorker();
  const result = await worker.recognize(image);
  return result.data.text;
}

/** Result of recognizing one field crop, with Tesseract's own confidence. */
export interface TextRecognition {
  text: string;
  /** Tesseract's page confidence (0..100), normalized to 0..1. */
  confidence: number;
}

/**
 * Recognize one field crop with Tesseract, returning its confidence — used as
 * the third, differently-engined pass in the field re-OCR cascade (see
 * ocr/field-review-engine.ts): a fundamentally different recognizer (classical
 * LSTM OCR) from the primary TrOCR handwriting model, so a field illegible to
 * one has a real chance with the other.
 */
export async function recognizeTextWithConfidence(image: Blob | File | HTMLCanvasElement | string): Promise<TextRecognition> {
  const worker = await getWorker();
  const result = await worker.recognize(image);
  return { text: result.data.text.trim(), confidence: (result.data.confidence ?? 0) / 100 };
}

/** Terminate and release the shared worker (frees memory). */
export async function terminateOcr(): Promise<void> {
  if (!workerPromise) return;
  const worker = await workerPromise;
  workerPromise = null;
  await worker.terminate();
}
